import { Component, DestroyRef, Inject, inject, input, OnChanges, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReportExportViewService } from '../../../../report-export-view.service';
import { ReportRecord } from '../../../../models/report.model';
import {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
  genThumbMediaURI,
  isMediaInfoPublishable,
  PropertyMediaInfo,
  MiqImagesModule,

} from '@miq/utils';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from '../../../../report-export-view.token';
import { NotificationService } from '../../../../shared/services/notification.service';
import { filter, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ReportImagesService } from '../../../../report-images.service';
import { ReportAllRecordsImage } from '../../../../models/template.model';
import { AdditionalImagesPosition } from '../../../../shared/templates/config/common';

import { MatIcon } from '@angular/material/icon';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'floor-plan',
  standalone: true,
  templateUrl: './floor-plan.component.html',
  styleUrl: './floor-plan.component.scss',
  imports: [L10nTranslationModule, MiqImagesModule, MatIcon],
})
export class FloorPlanComponent implements OnInit, OnChanges {
  readonly reportId = input<number>();
  readonly reportRecordData = input<ReportRecord>();
  readonly templateId = input<number>();

  public availabilityInfo = [];
  public edpMultimediaURI: string;
  public s3BucketImagesUrl: string;
  public selectedImageId: number;
  public reportAllImages: ReportAllRecordsImage;
  public floorPlanImages = [];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(REPORT_EXPORT_CONFIGS_TOKEN) reportModuleConfigs: ReportExportConfigurations,
    private readonly reportExportViewService: ReportExportViewService,
    public translationService: L10nTranslationService,
    private readonly notificationService: NotificationService,
    private readonly reportImagesService: ReportImagesService,
    private cdr: ChangeDetectorRef
  ) {
    this.edpMultimediaURI = reportModuleConfigs.edpMultimediaURI;
    this.s3BucketImagesUrl = reportModuleConfigs.s3BucketImagesUrl;
  }

  ngOnChanges() {
    const reportRecordData = this.reportRecordData();
    if (reportRecordData?.data) {
      // Handle object with numeric keys vs array vs single object
      if (Array.isArray(reportRecordData.data)) {
        this.availabilityInfo = reportRecordData.data;
      } else if (typeof reportRecordData.data === 'object') {
        // Check if it's an object with numeric keys (like {"0": {...}, "1": {...}})
        const keys = Object.keys(reportRecordData.data);
        const hasNumericKeys = keys.some(key => !isNaN(Number(key)));

        if (hasNumericKeys) {
          // Convert object with numeric keys to array
          this.availabilityInfo = keys
            .filter(key => !Number.isNaN(Number(key))) // Only numeric keys
            .sort((a, b) => Number(a) - Number(b)) // Sort by numeric value
            .map(key => reportRecordData.data[key]); // Get the objects
        } else {
          // Single object - wrap in array
          this.availabilityInfo = [reportRecordData.data];
        }
      } else {
        this.availabilityInfo = [reportRecordData.data];
      }
    }
    this.showFloorPlanImages();
  }

  ngOnInit() {
    this.reportImagesService.onUpdateAdditionalImage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.reportAllImages = response;
      this.showFloorPlanImages();
      this.cdr.markForCheck();
    });
  }

  public openDialog(availabilityId: number, method: 'add' | 'edit') {
    const reportId = this.reportId();
    const availabilityInfo = this.availabilityInfo.find(availability => availability.entity.id == availabilityId);
    const imagesToDisplay: PropertyMediaInfo[] = this.getFloorPlanImages(availabilityInfo);
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
      navigationMessage: this.translationService.translate('core.navigationMessage'),
      addNew: this.translationService.translate('recordsSearch.addNew'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId: this.reportAllImages[availabilityInfo.entity.id]
        ? this.reportAllImages[availabilityInfo.entity.id][0].imageId
        : null,
      translatedData,
      navigationMessage: true,
      availabilityId,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          let addEdit$: Observable<any> = of();
          if (method === 'add') {
            addEdit$ = this.reportExportViewService.addAdditionalImage(reportId, [
              { edpRecordId: availabilityInfo.entity.id, imageId: image.id },
            ]);
          } else {
            const reportImageId =
              this.reportAllImages[availabilityInfo.entity.id][AdditionalImagesPosition.AdditionalImage].reportImageId;
            addEdit$ = this.reportExportViewService.changeAdditionalImage(reportId, reportImageId, image.id);
          }
          return addEdit$;
        })
      )
      .subscribe((repImageId: number | number[]) => {
        const reportImageId = Array.isArray(repImageId) ? repImageId[0] : repImageId;
        if (method === 'add') {
          this.reportAllImages[availabilityInfo.entity.id] = [
            {
              reportImageId,
              imageId: selectedImage.id,
            },
          ];
        } else {
          this.reportAllImages[availabilityInfo.entity.id][0] = {
            reportImageId,
            imageId: selectedImage.id,
          };
        }
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.cdr.markForCheck();
      });
  }

  public deleteFloorPlan(floorPlanInfo: { id: number }) {
    const reportId = this.reportId();
    const reportImageId = this.reportAllImages[floorPlanInfo.id][0].reportImageId;
    this.reportExportViewService.deleteAdditionalImage(reportId, reportImageId).subscribe(() => {
      delete this.reportAllImages[floorPlanInfo.id];
      this.reportImagesService.setReportRecordImages(this.reportAllImages);
      this.showFloorPlanImages();
      this.cdr.markForCheck();
    });
  }

  public navigate(availabilityId: number) {
    const url = `/records/availability/${availabilityId}/edit`;
    window.open(url, '_blank');
  }

  private getFloorPlanImages(availability) {
    let imagesToDisplay: PropertyMediaInfo[] = [];
    const mediaInfo = availability.getMediaInformation();
    if (mediaInfo.length) {
      imagesToDisplay = mediaInfo
        .filter(media => media.media_content_type_desc === 'Floor Plan' && media.publish_image_f)
        .sort((a, b) => a.display_order - b.display_order);
    }
    return imagesToDisplay;
  }

  private showFloorPlanImages() {
    this.floorPlanImages = [];
    if (this.availabilityInfo.length) {
      this.availabilityInfo.forEach((availability, index) => {
        const availabilityMediaInfo = availability.getMediaInformation();
        const selectedMedia = (availabilityMediaInfo || []).find(media => {
          let imageInfo = this.reportAllImages?.[availability.entity.id]
            ? this.reportAllImages[availability.entity.id].find(image => image.imageId === media.id)
            : null;
          return imageInfo || null;
        });
        let data;
        if (selectedMedia) {
          data = {
            id: availability.entity.id,
            position: index + 1,
            imageSrc: genThumbMediaURI(this.edpMultimediaURI, this.s3BucketImagesUrl, selectedMedia.media_path, {
              provideDefault: true,
            }),
            publishable: isMediaInfoPublishable(availability),
            floor_suite: {
              floor_number: availability.floor_suite.floor_number,
              suite_number: availability.floor_suite.suite_number,
            },
          };
        } else {
          const availabilityImages = (availabilityMediaInfo || []).filter(
            media => media.media_content_type_desc === 'Floor Plan' && media.publish_image_f
          );
          data = {
            id: availability.entity.id,
            position: index + 1,
            availabilityImages: availabilityImages.length,
            floor_suite: {
              floor_number: availability.floor_suite.floor_number,
              suite_number: availability.floor_suite.suite_number,
            },
          };
        }
        this.floorPlanImages.push(data);
      });
    }
  }
}
